import { Component, computed, effect, input, output, signal } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { QaiAlertComponent, QaiButtonComponent, QaiLoadingComponent } from '@qai/ui';
import { SimulationGenerationPollingService } from 'src/app/services/self-serve/simulation-generation-polling.service';
import { SimulationGenerationStatus } from 'src/app/utils/enums/self-serve/simulation-generation-status.enum';
import { GeneratedSimulationDetailsModalComponent } from 'src/app/pages/self-serve/simulation-builder/components/modals/generated-simulation-details-modal/generated-simulation-details-modal.component';
import { AvatarSelectionModalComponent } from 'src/app/pages/self-serve/simulation-builder/components/modals/avatar-selection-modal/avatar-selection-modal.component';
import { SelfAuthoringPersonaFormService } from 'src/app/pages/self-serve/simulation-builder/services/self-authoring-persona-form.service';
@Component({
  selector: 'simulation-generation-modal',
  standalone: true,
  templateUrl: './simulation-generation-modal.component.html',
  styleUrls: ['./simulation-generation-modal.component.scss'],
  imports: [
    QaiLoadingComponent,
    QaiAlertComponent,
    QaiButtonComponent,
    GeneratedSimulationDetailsModalComponent,
    AvatarSelectionModalComponent,
    TranslocoModule,
  ],
})
export class SimulationGenerationModalComponent {
  callUuid = input<string>();
  moduleUuid = input<string>();
  retryInProgress = input(false);
  backToForm = output<void>();
  backToFormWithPrefill = output<void>();
  reviewModalClosed = output<void>();
  pollingTimedOut = output<void>();
  retryClicked = output<void>();

  /** Opens automatically (after REVIEW_MODAL_OPEN_DELAY_MS) once generation
   * completes, so the review modal shows without waiting for the user to
   * click "Test Simulation". While open, this modal's own body/buttons hide. */
  reviewModalOpen = signal(false);
  avatarSelectionModalOpen = signal(false);

  private static readonly REVIEW_MODAL_OPEN_DELAY_MS = 1000;

  private readonly stageMessageKeys: Partial<Record<string, string>> = {
    [SimulationGenerationStatus.ProcessingDocuments]:
      'simulationBuilder.PROCESSING_DOCUMENTS_MESSAGE',
    [SimulationGenerationStatus.GenerationCoreSignal]:
      'simulationBuilder.CORE_SIGNAL_MESSAGE',
    [SimulationGenerationStatus.GenerationSectionPlan]:
      'simulationBuilder.SECTION_PLAN_MESSAGE',
    [SimulationGenerationStatus.GenerationDerivedComponent]:
      'simulationBuilder.DERIVED_COMPONENT_MESSAGE',
    [SimulationGenerationStatus.GenerationComponentWave1]:
      'simulationBuilder.COMPONENT_WAVE_1_MESSAGE',
    [SimulationGenerationStatus.GenerationComponentWave2]:
      'simulationBuilder.COMPONENT_WAVE_2_MESSAGE',
    [SimulationGenerationStatus.GenerationComponentWave3]:
      'simulationBuilder.COMPONENT_WAVE_3_MESSAGE',
    [SimulationGenerationStatus.GenerationMessageAdherence]:
      'simulationBuilder.MESSAGE_ADHERENCE_MESSAGE',
    [SimulationGenerationStatus.Assembling]:
      'simulationBuilder.ASSEMBLING_MESSAGE',
    [SimulationGenerationStatus.Completed]:
      'simulationBuilder.FINISHING_SIMULATION_MESSAGE',
  };
  private readonly DEFAULT_STAGE_MESSAGE_KEY =
    'simulationBuilder.PROCESSING_DOCUMENTS_MESSAGE';

  constructor(
    private pollingService: SimulationGenerationPollingService,
    private translocoService: TranslocoService,
    private selfAuthoringPersonaFormService: SelfAuthoringPersonaFormService,
  ) {
    effect(() => {
      if (this.hasPollingTimedOut()) {
        this.pollingTimedOut.emit();
      }
    });

    effect((onCleanup) => {
      if (!this.isCompleted()) return;

      const timeoutId = setTimeout(
        () => this.reviewModalOpen.set(true),
        SimulationGenerationModalComponent.REVIEW_MODAL_OPEN_DELAY_MS,
      );
      onCleanup(() => clearTimeout(timeoutId));
    });
  }

  callPersonaForm = computed(() => {
    const callUuid = this.callUuid();
    return callUuid
      ? this.selfAuthoringPersonaFormService.getOrCreateForm(callUuid)
      : undefined;
  });

  job = computed(() =>
    this.pollingService.getGeneration(this.callUuid() ?? ''),
  );
  hasPollingTimedOut = computed(() =>
    this.pollingService.isPollingCapReached(this.callUuid() ?? ''),
  );
  isFailed = computed(
    () => this.job()?.status === SimulationGenerationStatus.Failed,
  );
  isCompleted = computed(
    () => this.job()?.status === SimulationGenerationStatus.Completed,
  );
  isTokenLimitExceeded = computed(
    () => this.job()?.status === SimulationGenerationStatus.TokenLimitExceeded,
  );
  isInProgress = computed(
    () =>
      !!this.job() &&
      !this.isFailed() &&
      !this.isCompleted() &&
      !this.isTokenLimitExceeded(),
  );
  isTakingLonger = computed(() =>
    this.pollingService.isTakingLong(this.callUuid() ?? ''),
  );

  currentStageLabel = computed(() =>
    this.translocoService.translate(
      this.stageMessageKeys[this.job()?.status ?? ''] ??
        this.DEFAULT_STAGE_MESSAGE_KEY,
    ),
  );

  handleBackClick(): void {
    this.backToForm.emit();
  }

  handleBackWithPrefillClick(): void {
    this.backToFormWithPrefill.emit();
  }

  handleReviewModalClosed(): void {
    if (this.avatarSelectionModalOpen()) {
      return;
    }
    this.reviewModalClosed.emit();
  }

  handleRetryClick(): void {
    this.retryClicked.emit();
  }

  handleSelectAvatarClick(): void {
    this.reviewModalOpen.set(false);
    this.avatarSelectionModalOpen.set(true);
  }

  handleBackToSimulationDetails(): void {
    this.avatarSelectionModalOpen.set(false);
    this.reviewModalOpen.set(true);
  }

  handleAvatarSelectionDismissed(): void {
    this.avatarSelectionModalOpen.set(false);
    this.reviewModalClosed.emit();
  }
}
